import { useEffect, useState } from 'react';
import {
  AddBrokerUserPayload, BrokerUser, CompanyUsers, defaultCompanyUser, UpdateBrokerUserPayload,
} from '../broker';
import useBrokerApi from '../../api/useBrokerApi';

export const useManageUser = (companyId: string) => {
  const [companyUsers, setCompanyUsers] = useState<CompanyUsers>(defaultCompanyUser);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [editingUserIds, setEditingUserIds] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const [openUsers, setOpenUsers] = useState<BrokerUser[]>([]);
  const [resetPagination, setResetPagination] = useState(false);

  const {
    getCompanyUsers, putReactivateUser, putDeactivateUser, postResendRegistrationToken, postAddCompanyUser, putUpdateCompanyUser,
  } = useBrokerApi();

  const handleResetPagination = () => {
    setResetPagination(!resetPagination);
  };

  const addNewUser = (user: BrokerUser) => {
    setOpenUsers((prev) => ([user, ...prev]));
    setExpandedRows([user.id]);
    handleResetPagination();
  };

  const deleteUser = (userId: string) => {
    setOpenUsers((prev) => prev.filter(({ id }) => id !== userId));
  };

  const setEditUser = (userId: string) => {
    setEditingUserIds((prev) => (prev.includes(userId) ? [...prev] : [...prev, userId]));
    setExpandedRows([userId]);
  };

  const unsetEditUser = (userId: string) => {
    setEditingUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const verifyUser = async (user: AddBrokerUserPayload) => {
    await postAddCompanyUser(user);
    const users = await getCompanyUsers(companyId);
    setCompanyUsers({ ...users });
    return users;
  };

  const updateUser = async (user: UpdateBrokerUserPayload) => {
    const data = await putUpdateCompanyUser(user);
    if (data.errors) throw data;

    setCompanyUsers({ ...data });
    return data;
  };

  const reactivateUser = async (userEmail: string) => {
    const data = await putReactivateUser(userEmail);
    setCompanyUsers({ ...data });
    return data;
  };

  const deactivateUser = async (userEmail: string) => {
    const data = await putDeactivateUser(userEmail);
    setCompanyUsers({ ...data });
    return data;
  };

  const resendRegistrationToken = async (userEmail: string) => postResendRegistrationToken(userEmail);

  useEffect(() => {
    (async () => {
      if (companyId) {
        try {
          setIsFetchingUsers(true);
          const data = await getCompanyUsers(companyId);
          setCompanyUsers({ ...data });
        } finally {
          setIsFetchingUsers(false);
        }
      }
    })();
  }, [companyId]);

  return {
    companyId,
    companyUsers,
    openUsers,
    editingUserIds,
    expandedRows,
    isFetchingUsers,
    resetPagination,
    addNewUser,
    updateUser,
    deleteUser,
    setEditUser,
    unsetEditUser,
    verifyUser,
    reactivateUser,
    deactivateUser,
    resendRegistrationToken,
  };
};
